/**
 * Krippendorff's alpha for any number of coders, any scale, and missing values.
 *
 * Follows Krippendorff (2011), "Computing Krippendorff's Alpha-Reliability": each unit
 * (item) contributes the ordered pairs of its observed values, each pair weighted
 * 1 / (m − 1) where m is the number of values observed in that unit. Units with fewer than
 * two values cannot be paired and are set aside. The weighted pair counts form the
 * coincidence matrix o, with marginals n_c, and
 *
 *   α = 1 − D_o / D_e,   D_o = Σ o_ck δ²_ck / n,   D_e = Σ n_c n_k δ²_ck / (n (n − 1)),
 *
 * which is the same thing as the textbook form 1 − (n − 1) Σ o_ck δ²_ck / Σ n_c n_k δ²_ck.
 *
 * The difference functions δ²:
 * - nominal  0 if c = k, else 1
 * - ordinal  (n_c/2 + n_(c+1) + … + n_(k−1) + n_k/2)², counting values of the categories
 *            between c and k, which is what `irr::kripp.alpha` computes
 * - interval (c − k)²
 * - ratio    ((c − k) / (c + k))², which needs non-negative values with a true zero
 */

import { parseNumberInput } from "@/lib/parse-number";

export type AlphaMetric = "nominal" | "ordinal" | "interval" | "ratio";

export const ALPHA_METRICS: readonly AlphaMetric[] = [
  "nominal",
  "ordinal",
  "interval",
  "ratio",
];

export interface ReliabilityData {
  /** Distinct values, in order: numeric order when every value is a number. */
  labels: string[];
  /** The numeric value of each label, or null when any value is text. */
  numbers: number[] | null;
  /** Units with two or more observed values, as indexes into `labels`. */
  units: number[][];
  totalUnits: number;
  droppedUnits: number;
  /** How many observations sat in units that were dropped. */
  droppedValues: number;
}

/**
 * Turn rows of cells (a unit per row, "" for a missing value) into category indexes.
 * Every cell is trimmed text; numbers are recognised through `parseNumberInput`.
 */
export function prepareReliabilityData(
  rows: readonly (readonly string[])[]
): ReliabilityData {
  const cleaned = rows.map((row) => row.filter((cell) => cell !== ""));
  const distinct = [...new Set(cleaned.flat())];
  const parsed = distinct.map((cell) => parseNumberInput(cell));
  const allNumeric = distinct.length > 0 && parsed.every((v) => v !== null);
  let labels: string[];
  let numbers: number[] | null = null;
  const index = new Map<string, number>();
  if (allNumeric) {
    const byNumber = new Map<number, string>();
    for (const [i, cell] of distinct.entries()) {
      byNumber.set(parsed[i] as number, cell);
    }
    const sortedNumbers = [...byNumber.keys()].sort((a, b) => a - b);
    numbers = sortedNumbers;
    labels = sortedNumbers.map(String);
    for (const cell of distinct) {
      index.set(cell, sortedNumbers.indexOf(parseNumberInput(cell) as number));
    }
  } else {
    labels = distinct;
    for (const [i, cell] of distinct.entries()) {
      index.set(cell, i);
    }
  }
  const units: number[][] = [];
  let droppedUnits = 0;
  let droppedValues = 0;
  for (const row of cleaned) {
    if (row.length >= 2) {
      units.push(row.map((cell) => index.get(cell) as number));
    } else {
      droppedUnits += 1;
      droppedValues += row.length;
    }
  }
  return {
    labels,
    numbers,
    units,
    totalUnits: rows.length,
    droppedUnits,
    droppedValues,
  };
}

/** The coincidence matrix o: ordered pairs within a unit, each weighted 1 / (m − 1). */
export function coincidenceMatrix(
  units: readonly (readonly number[])[],
  categories: number
): number[][] {
  const o = Array.from({ length: categories }, () =>
    new Array<number>(categories).fill(0)
  );
  for (const unit of units) {
    const m = unit.length;
    if (m < 2) {
      continue;
    }
    const weight = 1 / (m - 1);
    for (let i = 0; i < m; i++) {
      for (let j = 0; j < m; j++) {
        if (i !== j) {
          o[unit[i]][unit[j]] += weight;
        }
      }
    }
  }
  return o;
}

export function marginals(o: readonly (readonly number[])[]): number[] {
  return o.map((row) => row.reduce((sum, value) => sum + value, 0));
}

/** The squared difference between every pair of categories under a metric. */
export function differenceMatrix(
  metric: AlphaMetric,
  numbers: readonly number[] | null,
  counts: readonly number[]
): number[][] {
  const q = counts.length;
  const prefix = [0];
  for (const count of counts) {
    prefix.push((prefix.at(-1) ?? 0) + count);
  }
  return Array.from({ length: q }, (_, c) =>
    Array.from({ length: q }, (__, k) => {
      if (c === k) {
        return 0;
      }
      if (metric === "nominal" || numbers === null) {
        return 1;
      }
      const a = numbers[c];
      const b = numbers[k];
      if (metric === "interval") {
        return (a - b) ** 2;
      }
      if (metric === "ratio") {
        return ((a - b) / (a + b)) ** 2;
      }
      const lo = Math.min(c, k);
      const hi = Math.max(c, k);
      const between =
        prefix[hi + 1] - prefix[lo] - (counts[lo] + counts[hi]) / 2;
      return between ** 2;
    })
  );
}

export interface AlphaResult {
  alpha: number;
  observedDisagreement: number;
  expectedDisagreement: number;
  /** Pairable values, n. */
  n: number;
}

/** α from a coincidence matrix; NaN when it is undefined (one category, or n < 2). */
export function alphaFromCoincidences(
  o: readonly (readonly number[])[],
  metric: AlphaMetric,
  numbers: readonly number[] | null
): AlphaResult {
  const counts = marginals(o);
  const n = counts.reduce((sum, value) => sum + value, 0);
  const delta = differenceMatrix(metric, numbers, counts);
  let observed = 0;
  let expected = 0;
  for (const [c, row] of o.entries()) {
    for (const [k, value] of row.entries()) {
      observed += value * delta[c][k];
      expected += counts[c] * counts[k] * delta[c][k];
    }
  }
  const observedDisagreement = observed / n;
  const expectedDisagreement = n > 1 ? expected / (n * (n - 1)) : Number.NaN;
  const alpha =
    expectedDisagreement > 0
      ? 1 - observedDisagreement / expectedDisagreement
      : Number.NaN;
  return { alpha, observedDisagreement, expectedDisagreement, n };
}

/** Whether a metric can be computed for this data: text allows only nominal. */
export function metricAvailable(
  metric: AlphaMetric,
  data: ReliabilityData
): boolean {
  if (metric === "nominal") {
    return true;
  }
  if (data.numbers === null) {
    return false;
  }
  return metric !== "ratio" || data.numbers.every((value) => value >= 0);
}

export interface AlphaSummary {
  metric: AlphaMetric;
  result: AlphaResult;
}

export function alphaByMetric(data: ReliabilityData): AlphaSummary[] {
  const o = coincidenceMatrix(data.units, data.labels.length);
  return ALPHA_METRICS.filter((metric) => metricAvailable(metric, data)).map(
    (metric) => ({
      metric,
      result: alphaFromCoincidences(o, metric, data.numbers),
    })
  );
}

/**
 * A small seeded generator (Park–Miller "minstd", multiplier 48271), so a bootstrap gives
 * the same interval every time. Plain arithmetic: every product stays below 2^53.
 */
export function seededRandom(seed: number): () => number {
  const modulus = 2_147_483_647;
  let state = Math.floor(Math.abs(seed)) % modulus;
  if (state === 0) {
    state = 1;
  }
  return () => {
    state = (state * 48_271) % modulus;
    return (state - 1) / (modulus - 1);
  };
}

export interface BootstrapResult {
  lower: number;
  upper: number;
  /** Resamples that gave a defined α. */
  valid: number;
  replicates: number;
  /** Share of resampled alphas below Krippendorff's two benchmarks. */
  below667: number;
  below800: number;
}

/**
 * Percentile bootstrap over units: draw the same number of units with replacement,
 * recompute α, repeat. A resample where every drawn value is identical has no α and is
 * skipped.
 */
export function bootstrapAlpha(
  data: ReliabilityData,
  metric: AlphaMetric,
  replicates = 2000,
  seed = 20_110_101,
  level = 0.95
): BootstrapResult | null {
  const { units, labels, numbers } = data;
  if (units.length < 2) {
    return null;
  }
  const random = seededRandom(seed);
  const alphas: number[] = [];
  for (let r = 0; r < replicates; r++) {
    const sample = units.map(
      () =>
        units[Math.min(units.length - 1, Math.floor(random() * units.length))]
    );
    const { alpha } = alphaFromCoincidences(
      coincidenceMatrix(sample, labels.length),
      metric,
      numbers
    );
    if (Number.isFinite(alpha)) {
      alphas.push(alpha);
    }
  }
  if (alphas.length < 20) {
    return null;
  }
  alphas.sort((a, b) => a - b);
  const tail = (1 - level) / 2;
  const at = (p: number) =>
    alphas[
      Math.min(alphas.length - 1, Math.max(0, Math.floor(p * alphas.length)))
    ];
  const share = (limit: number) =>
    alphas.filter((value) => value < limit).length / alphas.length;
  return {
    lower: at(tail),
    upper: at(1 - tail),
    valid: alphas.length,
    replicates,
    below667: share(2 / 3),
    below800: share(0.8),
  };
}

export type ReliabilityBand = "unreliable" | "tentative" | "reliable";

/** Krippendorff (2004): α ≥ .800 reliable; .667 ≤ α < .800 tentative; below, discard. */
export function reliabilityBand(alpha: number): ReliabilityBand {
  if (alpha >= 0.8) {
    return "reliable";
  }
  return alpha >= 2 / 3 ? "tentative" : "unreliable";
}
